import React from 'react';
import { motion } from 'framer-motion';
import { Wifi, Zap, Cloud, Shield } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const WhyChooseSection = () => {
  const { t } = useLanguage();

  const benefits = [
    {
      icon: Wifi,
      title: t('why_hardware_title') || 'No Hardware Required',
      desc: t('why_hardware_desc') || 'Works on any tablet, phone, or computer you already own. No expensive terminals needed.',
      accent: 'bg-blue-50 text-blue-600 border-blue-100',
    },
    {
      icon: Zap,
      title: t('why_setup_title') || 'Setup in Minutes',
      desc: t('why_setup_desc') || 'Sign up, add your products, and start selling today. No technician visits or complex configuration.',
      accent: 'bg-amber-50 text-amber-600 border-amber-100',
    },
    {
      icon: Cloud,
      title: t('why_cloud_title') || 'Cloud-Based',
      desc: t('why_cloud_desc') || 'Access your data from anywhere. Updates happen automatically. Always running the latest version.',
      accent: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    },
    {
      icon: Shield,
      title: t('why_secure_title') || 'Secure & Reliable',
      desc: t('why_secure_desc') || 'Bank-level encryption. Daily backups. Your business data is always protected and available.',
      accent: 'bg-purple-50 text-purple-600 border-purple-100',
    },
  ];

  return (
    <section className="py-24 md:py-32 bg-white" data-testid="why-choose-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1e3a5f] tracking-tight mb-4">
            {t('why_title') || 'Why Shop Owners Choose VendFactura'}
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {t('why_subtitle') || 'We\'ve removed every barrier to getting started. No expensive hardware, no complex setup, no long-term contracts.'}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((b, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-white border-2 border-slate-100 p-8 hover:border-[#1e3a5f]/20 transition-colors"
              data-testid={`why-benefit-${i}`}
            >
              <div className={`w-14 h-14 ${b.accent} border-2 flex items-center justify-center mb-5`}>
                <b.icon className="w-7 h-7" />
              </div>
              <h3 className="text-lg font-bold text-[#1e3a5f] mb-2">{b.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{b.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
